import AlarmIcon from "@mui/icons-material/Alarm";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import TimerIconOutlined from "@mui/icons-material/TimerOutlined";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { gray } from "../../../../theme/themePrimitives";
import { AssignmentsWidget } from "../../../Analytics/Dashboard/components/AssignmentsWidget";
import { useCustomisation } from "../../../../context/CustomisationContext";
import { getShadeColour } from "../../../../utils/helpers";
import useScreenSize from "../../../../hooks/useScreenSize";
import { useAuth } from "../../../../context/AuthContext";
import { usePortal } from "../../../../context/PortalsContext";
import { getDashboard } from "../../../../services/api/apiClient";
import { DashboardUserAssignmentsResult } from "../../../../services/api/types/dashboard";
import { HomeBannerSkeleton } from "./HomeBannerSkeleton";

interface HomeBannerProps {}

const ICON_MAP: Record<string, React.ComponentType> = {
  completedAssignments: CheckBoxOutlinedIcon,
  averageScore: LeaderboardOutlinedIcon,
  totalMinutesOfLearning: TimerIconOutlined,
};

export type ShadowSize = "N/A" | "XS" | "SM" | "MD" | "LG";

export const HomeBanner: React.FC<HomeBannerProps> = () => {
  const [expanded, setExpanded] = useState(false);
  const [dashboardData, setDashboardData] =
    useState<DashboardUserAssignmentsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const { isMobile, isTablet } = useScreenSize();
  const { customisations } = useCustomisation();
  const { auth } = useAuth();
  const { portals } = usePortal();
  const { HOME, GPC } = customisations.customisations;

  const secondaryTextColor =
    HOME.textColorsSettings?.homeSecondaryTextColor ||
    GPC.textColorsSettings?.gpcSecondaryTextColor;
  const bgColor =
    HOME.assignmentStatusColorsSettings?.homeCompleted ||
    GPC.assignmentStatusColorsSettings?.gpcCompleted;
  const secondaryBgColor =
    HOME.backgroundSettings?.homeSecondaryBgColor ||
    GPC.backgroundSettings?.gpcSecondaryBgColor;
  const borderSize =
    HOME.assignmentStatusColorsSettings?.homeBorderSize ||
    GPC.assignmentStatusColorsSettings?.gpcBorderSize;
  const borderColor =
    HOME.assignmentStatusColorsSettings?.homeBorderColor ||
    GPC.assignmentStatusColorsSettings?.gpcBorderColor;
  const shadowSize =
    HOME.assignmentStatusColorsSettings?.homeShadowSize ||
    GPC.assignmentStatusColorsSettings?.gpcShadowSize;
  const shadowColor =
    HOME.assignmentStatusColorsSettings?.homeShadowColor ||
    GPC.assignmentStatusColorsSettings?.gpcShadowColor;
  const linkColor =
    HOME.buttonsSettings?.homeLinkColor || GPC.buttonsSettings?.gpcLinkColor;

  const shadowsMap: Record<ShadowSize, string | null> = {
    "N/A": null,
    XS: `0 1px 2px 0 ${shadowColor}`,
    SM: `0 1px 3px 0 ${shadowColor}`,
    MD: `0 4px 8px -2px ${shadowColor}`,
    LG: `0 12px 16px -4px ${shadowColor}`,
  };

  useEffect(() => {
    const fetchDashboard = async () => {
      if (auth.user?.UserPortalUid && portals.selectedPortal?.portalUid) {
        try {
          setLoading(true);
          const result = await getDashboard(
            portals.selectedPortal.portalUid,
            auth.user.UserPortalUid,
          );
          if ("dashboardAssignments" in result) {
            setDashboardData(result as DashboardUserAssignmentsResult);
          }
        } catch (error) {
          console.error("Error fetching dashboard data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDashboard();
  }, [auth.user?.UserPortalUid, portals.selectedPortal?.portalUid]);

  const assignments = dashboardData?.dashboardAssignments;
  const data = assignments
    ? [
        {
          id: 0,
          value: assignments.completedAssignments,
          label: "Completed",
        },
        {
          id: 1,
          value: assignments.pendingAssignments,
          label: "Pending",
        },
        {
          id: 2,
          value: assignments.overdueAssignments,
          label: "Overdue",
        },
      ]
    : [];

  const cards = dashboardData
    ? [
        {
          id: "completedAssignments",
          title: "Assignments completed",
          amount: dashboardData.numberOfCompletedAssignments,
        },
        {
          id: "averageScore",
          title: "Average score",
          amount: `${dashboardData.averageScore}%`,
        },
        {
          id: "totalMinutesOfLearning",
          title: "Minutes of learning",
          amount: dashboardData.totalMinutesOfLearning,
        },
      ]
    : [];

  const transformedData = data.map((item: any, index: number) => ({
    id: item.id || index,
    value: item.value,
    label: item.label,
  }));
  const total = data.reduce((sum: number, item: any) => sum + item.value, 0);
  const chartWidget = (
    <AssignmentsWidget
      data={transformedData}
      total={total}
      withoutWrapper={true}
      title="Total"
      centerContent={isMobile}
    />
  );

  const toggleExpanded = () => setExpanded(!expanded);
  const getCardIcon = (id: string) => {
    const IconComponent = ICON_MAP[id] || AlarmIcon;
    return <IconComponent />;
  };

  if (loading || !dashboardData) {
    return <HomeBannerSkeleton />;
  }

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: secondaryBgColor,
          borderRadius: "12px",
          p: 2,
          height: "100%",
        }}
      >
        <Box sx={{ width: "100%", padding: "1rem 0" }}>{chartWidget}</Box>

        <Collapse
          sx={{ width: "100%" }}
          in={expanded}
          timeout="auto"
          unmountOnExit
        >
          {cards.map((card: any, index: number) => (
            <Stack
              key={index}
              sx={{
                borderTop: `1px solid #e0e0e0`,
                px: "1rem",
                py: 2,
                minWidth: 0,
                overflow: "hidden",
                alignItems: "center",
              }}
              spacing={2}
            >
              <Typography
                color={secondaryTextColor}
                variant="h1"
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {card.amount}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: secondaryTextColor,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {card.title}
              </Typography>
            </Stack>
          ))}
        </Collapse>

        <Box
          sx={{
            mt: "auto",
            pt: 2,
            width: "100%",
            borderTop: `1px solid #e0e0e0`,
          }}
        >
          <Stack direction="row" justifyContent="center" alignItems="center">
            <Typography
              variant="body2"
              onClick={toggleExpanded}
              sx={{
                cursor: "pointer",
                fontWeight: 500,
                mr: 1,
                color: linkColor,
              }}
            >
              {expanded ? "Fold" : "Expand"}
            </Typography>
            <IconButton
              sx={{ border: "none" }}
              size="small"
              onClick={toggleExpanded}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Box>
      </Box>
    );
  }

  if (isTablet) {
    return (
      <Box
        sx={{
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          backgroundColor: secondaryBgColor,
          borderRadius: "12px",
          border: `${borderSize} solid ${borderColor}`,
          boxShadow: shadowsMap[shadowSize as ShadowSize],
          p: 2,
          overflow: "hidden",
          "& > *": { minWidth: 0 },
        }}
      >
        <Box sx={{ width: "100%", pb: 2 }}>{chartWidget}</Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
          }}
        >
          {cards.map((card: any, index: number) => (
            <Stack
              key={index}
              spacing={0.5}
              sx={{
                minWidth: 0,
                px: index === 1 ? "6rem" : undefined,
                borderLeft: index === 1 ? `1px solid ${gray[300]}` : undefined,
                borderRight: index === 1 ? `1px solid ${gray[300]}` : undefined,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 600,
                  color: secondaryTextColor,
                  fontSize: { xs: "48px", sm: "52px" },
                  lineHeight: 1.2,
                }}
              >
                {card.amount}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: secondaryTextColor,
                  fontSize: "14px",
                }}
              >
                {card.title}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 1440,
        margin: "0 auto",
        display: "grid",
        gridTemplateColumns: "2fr 1fr 1fr 1fr",
        backgroundColor: secondaryBgColor,
        borderRadius: "12px",
        border: `${borderSize} solid ${borderColor}`,
        boxShadow: shadowsMap[shadowSize as ShadowSize],
        p: { xs: 1.5, sm: 2, md: 2 },
        overflow: "hidden",
        "& > *": {
          minWidth: 0,
        },
      }}
    >
      <Box>{chartWidget}</Box>
      {cards.map((card: any, index: number) => (
        <Stack
          key={index}
          sx={{
            borderLeft: `1px solid ${gray[300]}`,
            px: { xs: 2, sm: 2.5, md: 3 },
            py: 1,
            display: "flex",
            justifyContent: "center",
          }}
          spacing={4}
        >
          <IconButton
            disabled
            sx={{
              bgcolor: `${secondaryBgColor ? getShadeColour(secondaryBgColor, 30) : "transparent"} !important`,
              borderRadius: "8px",
              "& svg": { color: secondaryBgColor ?? bgColor },
              flexShrink: 0,
            }}
          >
            {getCardIcon(card.id)}
          </IconButton>
          <Stack gap={1} height={115}>
            <Typography
              color={secondaryTextColor}
              variant="h1"
              sx={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
                fontSize: { xs: "48px", sm: "52px", md: "56px", lg: "60px" },
                lineHeight: 1.2,
              }}
            >
              {card.amount}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                color: secondaryTextColor,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                fontSize: { xs: "12px", sm: "14px" },
              }}
            >
              {card.title}
            </Typography>
          </Stack>
        </Stack>
      ))}
    </Box>
  );
};
